// Integration Types for Frontend
// Self-contained type definitions for OAuth, providers, and connections

// ============================================
// Auth Mode Types
// ============================================

export interface AuthModes {
  OAuth1: 'OAUTH1';
  OAuth2: 'OAUTH2';
  OAuth2CC: 'OAUTH2_CC';
  Basic: 'BASIC';
  ApiKey: 'API_KEY';
  AppStore: 'APP_STORE';
  Custom: 'CUSTOM';
  App: 'APP';
  None: 'NONE';
  TBA: 'TBA';
  Jwt: 'JWT';
  Bill: 'BILL';
  TwoStep: 'TWO_STEP';
  Signature: 'SIGNATURE';
}

export type AuthModeType = AuthModes[keyof AuthModes];

// ============================================
// Credential Types
// ============================================

export interface BasicApiCredentials {
  type: 'BASIC';
  username: string;
  password: string;
}

export interface ApiKeyCredentials {
  type: 'API_KEY';
  apiKey: string;
}

export interface OAuth2ClientCredentials {
  type: 'OAUTH2_CC';
  token: string;
  expires_at?: Date;
  client_id: string;
  client_secret: string;
}

export interface JwtCredentials {
  type: 'JWT';
  [key: string]: any;
  token?: string;
  expires_at?: Date;
}

export interface BillCredentials {
  type: 'BILL';
  username: string;
  password: string;
  organization_id: string;
  dev_key: string;
}

export interface TwoStepCredentials {
  type: 'TWO_STEP';
  [key: string]: any;
  token?: string;
  refresh_token?: string;
  expires_at?: Date;
}

export interface SignatureCredentials {
  type: 'SIGNATURE';
  username: string;
  password: string;
  token?: string;
  expires_at?: Date;
}

export interface TbaCredentials {
  type: 'TBA';
  token_id: string;
  token_secret: string;
}

export type AllAuthCredentials =
  | BasicApiCredentials
  | ApiKeyCredentials
  | OAuth2ClientCredentials
  | JwtCredentials
  | BillCredentials
  | TwoStepCredentials
  | SignatureCredentials
  | TbaCredentials;

// ============================================
// Provider Types
// ============================================

export interface SimplifiedJSONSchema {
  type: 'string';
  title: string;
  description: string;
  example?: string;
  pattern?: string;
  optional?: boolean;
  format?: 'hostname' | 'uri' | 'uuid' | 'email';
  order: number;
  default_value?: string;
  prefix?: string;
  suffix?: string;
  doc_section?: string;
  secret?: string;
}

export interface BaseProvider {
  display_name: string;
  auth_mode: AuthModeType;
  proxy?: {
    base_url: string;
    headers?: Record<string, string>;
  };
  authorization_url?: string;
  default_scopes?: string[];
  token_url?: string;
  docs?: string;
  docs_connect?: string;
  categories?: string[];
  connection_config?: Record<string, SimplifiedJSONSchema>;
  credentials?: Record<string, SimplifiedJSONSchema>;
}

export type Provider = BaseProvider;

export interface ApiProvider extends BaseProvider {
  unique_key: string;
}

export interface ApiProviderListItem {
  unique_key: string;
  display_name: string;
  auth_mode: AuthModeType;
  categories?: string[];
}

export interface ApiPublicIntegration {
  unique_key: string;
  provider: string;
  display_name?: string;
  logo?: string;
  created_at: string;
  updated_at: string;
}

// ============================================
// OAuth Flow Types
// ============================================

export type AuthErrorType =
  | 'missing_auth_token'
  | 'blocked_by_browser'
  | 'window_closed'
  | 'connection_failed'
  | 'unknown_error';

export interface AuthResult {
  providerConfigKey: string;
  connectionId: string;
}

export type AuthSuccess = AuthResult;

export interface AuthOptions {
  detectClosedAuthWindow?: boolean;
  params?: Record<string, string>;
  authorization_params?: Record<string, string | undefined>;
}

export interface ConnectionConfig {
  params?: Record<string, string>;
  authorization_params?: Record<string, string | undefined>;
  credentials?: Record<string, unknown>;
}

// ============================================
// App-specific types for our API responses
// ============================================

// Connection status in our system
export type ConnectionStatus = 'active' | 'expired' | 'revoked';

// Credential field for dynamic form generation
export interface CredentialField {
  name: string;
  label: string;
  type: 'text' | 'password' | 'textarea';
  required: boolean;
  placeholder?: string;
  description?: string;
}

// Connection config field (subdomain, hostname, etc. needed before OAuth)
export interface ConnectionConfigField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  description?: string;
  prefix?: string;
  suffix?: string;
}

// Auth mode details from unified provider metadata
export interface AuthModeDetails {
  mode: AuthModeType;
  label: string;
  authorizationUrl?: string;
  tokenUrl?: string | Record<string, string>;
  defaultScopes?: string[];
  credentials?: CredentialField[];
  connectionConfig?: ConnectionConfigField[];
  baseUrl?: string;
  proxyHeaders?: Record<string, string>;
}

// Provider response from our API (GET /providers, GET /providers/:provider)
export interface ProviderResponse {
  unique_key: string;
  provider: string;
  display_name?: string;
  auth_mode: string;  // Primary auth mode (first/preferred)
  auth_modes?: AuthModeDetails[];  // All available auth modes
  logo: string;
  categories: string[];  // Array of category strings from backend
  docs_url?: string;
  docs_connect?: string;  // Documentation for connecting (e.g., how to get API keys)
  setup_guide_url?: string;  // Step-by-step setup guide URL
  credentials?: CredentialField[];  // Credentials for primary auth mode
  connection_config?: ConnectionConfigField[];  // Connection config for primary auth mode
  has_oauth?: boolean;
  default_scopes?: string[];
}

// User connection from our API (GET /user-connections)
export interface UserConnection {
  alias: string;
  provider_config_key: string;
  display_name: string | null;
  status: ConnectionStatus;
  is_default?: boolean;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Auth URL response from our API (GET /auth-url/:provider)
export interface AuthUrlResponse {
  url: string;
  state: string;
  connection_id: string;
  callback_url: string;
}

// Advanced configuration overrides (for custom deployments)
export interface AdvancedOverrides {
  base_url?: string;  // Custom proxy base URL for self-hosted/regional deployments
}

// Connect request for non-OAuth (POST /connect)
export interface ConnectRequest {
  provider: string;
  authMode?: string;  // Selected auth mode (e.g., 'API_KEY', 'BASIC')
  credentials: Record<string, string>;
  displayName?: string;
  connectionConfig?: Record<string, string>;  // User-provided config (subdomain, etc.)
  advancedOverrides?: AdvancedOverrides;
}

// Generic API response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// ============================================
// Helpers
// ============================================

// Check if auth mode requires OAuth popup
export function isOAuthMode(authMode: string): boolean {
  return authMode === 'OAUTH2' || authMode === 'OAUTH1';
}

// Provider categories for filtering
export const PROVIDER_CATEGORIES = [
  'All',
  'CRM',
  'AI',
  'Communication',
  'Development',
  'Productivity',
  'Support',
  'Payments',
  'Other',
] as const;

export type ProviderCategory = (typeof PROVIDER_CATEGORIES)[number];

// ============================================
// Gateway / Data Browser Types
// ============================================

/** Gateway API response structure */
export interface GatewayResponse<T = unknown> {
  success: boolean;
  data: T;
  provider: string;
  endpoint: string;
  status: number;
  filtered: boolean;
  metadata: {
    durationMs: number;
    timestamp: string;
  };
}

/** Column configuration for data table */
export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  order: number;
  width?: number;
}

/** Pagination state */
export interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
}

/** Data type detection result for smart cell rendering */
export type DetectedType =
  | 'url'
  | 'email'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'number'
  | 'array'
  | 'object'
  | 'null'
  | 'string';

// ============================================
// Data Browser / Endpoint Discovery Types
// ============================================

/** HTTP methods supported by the gateway */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Path parameter definition with optional default */
export interface PathParamDef {
  name: string;
  default?: string;        // Auto-fill value (e.g., "me" for userId)
  description?: string;    // Help text for user
  required?: boolean;      // If false, can be omitted
}

/** Provider endpoint from backend API */
export interface ProviderEndpoint {
  method: HttpMethod;
  path: string;
  description?: string;
  params?: string[];
  pathParams?: PathParamDef[];  // Path parameters with defaults
  body?: string;
  source?: string;
}

/** Provider endpoint group response */
export interface ProviderEndpointGroup {
  displayName: string;
  baseUrl?: string;
  endpoints: ProviderEndpoint[];
}

/** Request configuration for the request builder */
export interface RequestConfig {
  method: HttpMethod;
  endpoint: string;
  pathParams: Record<string, string>;
  queryParams: Record<string, string>;
  body?: string;
}

/** Execution result with technical details */
export interface ExecutionResult {
  success: boolean;
  data: unknown;
  status: number;
  durationMs: number;
  timestamp: string;
  provider: string;
  endpoint: string;
  error?: string;
}

/** Request history entry */
export interface RequestHistoryEntry {
  id: string;
  config: RequestConfig;
  result: ExecutionResult;
  timestamp: string;
}
